import { z } from 'zod';

export const CardFundingVerificationFailureKind = {
    INVALID_BIN: 'INVALID_BIN',
    UNAVAILABLE: 'UNAVAILABLE',
    SPECIFIC_FAILURE: 'SPECIFIC_FAILURE',
    GENERIC_FAILURE: 'GENERIC_FAILURE',
} as const;

export type CardFundingVerificationFailureKind =
    (typeof CardFundingVerificationFailureKind)[keyof typeof CardFundingVerificationFailureKind];

export interface CardFundingVerificationFailure {
    readonly kind: CardFundingVerificationFailureKind;
    readonly message: string | null;
}

export const CardFundingVerificationStringSchema: z.ZodType<string> = z.string();

function extractErrorMessage(error: unknown): string | null {
    if (error instanceof Error) {
        const message = error.message.trim();
        return message.length > 0 ? message : null;
    }
    const stringParseResult = CardFundingVerificationStringSchema.safeParse(error);
    if (stringParseResult.success) {
        const message = stringParseResult.data.trim();
        return message.length > 0 ? message : null;
    }
    return null;
}

function normalizeCredentialDecryptionFailure(message: string): string | null {
    const match = /^Failed to decrypt credentials(?::\s*(.+))?$/i.exec(message);
    if (match === null) {
        return null;
    }
    const reason = match[1]?.trim();
    if (reason === undefined || reason.length === 0) {
        return 'Payment processor credentials cannot be decrypted.';
    }
    return `Payment processor credentials cannot be decrypted: ${reason}.`;
}

function isInvalidBinLookupFailure(message: string): boolean {
    return /\bINVALID BIN\b|invalid (?:card|pan) prefix/i.test(message);
}

function isUnavailableCardFundingFailure(message: string): boolean {
    return /supported card type|funding source|temporarily unavailable|UNKNOWN|PREPAID/i.test(
        message
    );
}

export function classifyCardFundingVerificationFailure(
    error: unknown
): CardFundingVerificationFailure {
    const message = extractErrorMessage(error);
    if (message === null) {
        return {
            kind: CardFundingVerificationFailureKind.GENERIC_FAILURE,
            message: null,
        };
    }

    if (isInvalidBinLookupFailure(message)) {
        return {
            kind: CardFundingVerificationFailureKind.INVALID_BIN,
            message,
        };
    }
    if (isUnavailableCardFundingFailure(message)) {
        return {
            kind: CardFundingVerificationFailureKind.UNAVAILABLE,
            message,
        };
    }

    return {
        kind: CardFundingVerificationFailureKind.SPECIFIC_FAILURE,
        message: normalizeCredentialDecryptionFailure(message) ?? message,
    };
}
